import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal, WritableSignal } from '@angular/core';
import { CreatorResponseDto, FilterBusinessCreatorsDto } from "../../../services/creator/creator.service";
import { catchError, finalize, Observable, of, share, throwError } from "rxjs";
import { switchMap } from "rxjs/operators";
import { ShoutPlusCreator, BusinessCreatorsListResponse, BusinessCreatorsTimeline } from "../../models/business-creator";
import { HttpService } from "../../../services/http/http.service";

@Injectable({
  providedIn: 'root'
})
export class BusinessCreatorsService {

  selected_for_brief: WritableSignal<CreatorResponseDto[] | null> = signal(null);

  /** Coalesces concurrent POSTs for the same business + creator (e.g. double submit / multiple UI surfaces). */
  private readonly linkCreatorInflight = new Map<string, Observable<ShoutPlusCreator>>();

  constructor(
    private _httpService: HttpService
  ) { }

  linkCreatorWithBusiness(body: ShoutPlusCreator): Observable<ShoutPlusCreator> {
    const key = `${body.business_account_id}:${body.creator_id}`;
    const inflight = this.linkCreatorInflight.get(key);
    if (inflight) {
      return inflight;
    }

    const request$ = this._httpService.runHttpCall(
      "POST",
      `/shout-plus/business-creators`,
      "application/json",
      body
    ).pipe(
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 409) {
          return this.filterBusinessCreators({
            business_account_id: body.business_account_id,
            creator_id: body.creator_id,
            take: 1,
          } as FilterBusinessCreatorsDto).pipe(
            switchMap((res) => {
              const row = res?.data?.[0];
              return row ? of(row as ShoutPlusCreator) : throwError(() => err);
            }),
          );
        }
        return throwError(() => err);
      }),
      share(),
      finalize(() => this.linkCreatorInflight.delete(key)),
    );

    this.linkCreatorInflight.set(key, request$);
    return request$;
  }

  updateShoutPlusCreator(
    id: string,
    body: Partial<ShoutPlusCreator>
  ): Observable<ShoutPlusCreator> {
    return this._httpService.runHttpCall(
      'PATCH',
      `/shout-plus/business-creators/${id}`,
      'application/json',
      body
    );
  }


  filterBusinessCreators(filters: FilterBusinessCreatorsDto): Observable<BusinessCreatorsListResponse> {
    return this._httpService.runHttpCall(
      "POST",
      `/shout-plus/business-creators/filter-business-creators`,
      "application/json",
      filters
    )
  }

  filterBusinessCreatorsTimeline(filters: FilterBusinessCreatorsDto): Observable<BusinessCreatorsTimeline> {
    return this._httpService.runHttpCall(
      "POST",
      `/shout-plus/business-creators/business-creators-timeline`,
      "application/json",
      filters
    )
  }
}
